import { compareDecimalStrings, isDecimalString, normalizeDecimal } from "@/shared/utils/decimal";

import type { OtcPairLimit } from "../types/cryptoSwaps.types";

/** Money never leaves the decimal-string domain here: an 8-decimal amount does not survive a float. */

export type AmountCheck = "empty" | "invalid" | "precision" | "below_min" | "above_max" | "ok";

export interface AmountBounds {
  min: string | null;
  max: string | null;
}

export interface AmountRules {
  decimals: number;
  bounds?: AmountBounds | null;
}

/** `1000`, `1000.00` and `1000.00000000` are the same money; comparing them as strings would not say so. */
export function canonicalAmount(value: string): string {
  if (!isDecimalString(value)) return value;
  try {
    return normalizeDecimal(value);
  } catch {
    return value;
  }
}

export function fractionDigits(value: string): number {
  return (value.split(".")[1] ?? "").replace(/0+$/, "").length;
}

export function checkAmount(amount: string, rules: AmountRules): AmountCheck {
  const value = amount.trim();
  if (value.length === 0) return "empty";
  if (!isDecimalString(value) || value.startsWith("-")) return "invalid";
  if (compareDecimalStrings(value, "0") <= 0) return "invalid";
  if (fractionDigits(value) > rules.decimals) return "precision";
  const bounds = rules.bounds;
  if (bounds?.min && isDecimalString(bounds.min) && compareDecimalStrings(value, bounds.min) < 0) {
    return "below_min";
  }
  if (bounds?.max && isDecimalString(bounds.max) && compareDecimalStrings(value, bounds.max) > 0) {
    return "above_max";
  }
  return "ok";
}

/**
 * One range to show and enforce, only when every payer kind agrees on it. The order's payer kind is decided
 * server-side, so a disagreement is left to the backend rather than guessed here.
 */
export function resolvePairBounds(limits: OtcPairLimit[] | null | undefined): AmountBounds | null {
  if (!limits || limits.length === 0) return null;
  const [first, ...rest] = limits;
  const same = (a: string | null, b: string | null) =>
    (a === null && b === null) || (a !== null && b !== null && canonicalAmount(a) === canonicalAmount(b));
  const agree = rest.every(
    (limit) => same(limit.min_amount, first.min_amount) && same(limit.max_amount, first.max_amount),
  );
  if (!agree || (first.min_amount === null && first.max_amount === null)) return null;
  return { min: first.min_amount, max: first.max_amount };
}

/** True when `amount` is more than `available`. Unreadable input answers false: the backend still refuses. */
export function exceedsBalance(
  amount: string | null | undefined,
  available: string | null | undefined,
): boolean {
  if (!amount || !available || !isDecimalString(amount) || !isDecimalString(available)) return false;
  return compareDecimalStrings(amount, available) > 0;
}
